<script setup lang="ts">
/**
 * Bloc d'ACHAT d'un produit (sélecteur de variantes + options + barre
 * d'action collante). Utilisé par la page produit dédiée
 * `/boutique/[slug]/[productSlug]`.
 *
 * Toute la logique vit dans `useShopProductSelection` — ce composant n'est que
 * le rendu, ce qui garantit que les corrections d'audit ne peuvent pas
 * diverger d'une surface à l'autre :
 *
 *  a) Épuisé ≠ inexistant — badge textuel « Épuisé » (canon
 *     `BslugStepTickets`, JAMAIS de barré) pour une valeur existante mais
 *     partie ; désactivation + `title` explicite pour une combinaison qui
 *     n'existe pas.
 *  b) A11y — `aria-disabled` + `aria-label` explicites, contrastes tenus
 *     (texte gris 500/400 + bordure, pas d'`opacity-40` illisible).
 *     Règle projet `ui_no_bg_transparency` : AUCUNE couleur semi-transparente
 *     ici — que des aplats pleins, et du noir sur blanc partout où la couleur
 *     ne porte pas de sens (l'ambre reste l'alerte « ventes fermées », le
 *     rouge l'épuisement). Un état désactivé se signale par sa COULEUR, pas
 *     par un voile d'`opacity` qui casse le contraste.
 *  c) Bouton TOUJOURS actif — au clic incomplet : scroll vers le 1er axe
 *     manquant + mise en évidence + message qui NOMME le critère.
 *  d) Prix à portée immédiate de l'action, « À partir de X € » tant qu'aucune
 *     variante n'est choisie.
 *  e) Stock affiché dès qu'il est calculable (sélection partielle comprise).
 *  f) Barre d'action COLLANTE en bas, mobile comme desktop.
 */
import type { EventCustomFieldDef } from "~/types/event";
import type { ShopProduct, ShopProductVariant } from "~/types/shop";
import { useShopProductSelection } from "~/composables/useShopProductSelection";
import { useUrlEnvoiDocument } from "~/composables/useEnvoiDocumentFormulaire";
import {
  resolveShopColorSwatch,
  shouldRenderAsColorSwatches,
  swatchInk,
  swatchInkHalo,
  swatchNeedsOutline,
  type ShopColorSwatch,
} from "~/utils/shopColorSwatch";

// Montants et dates dans la langue de la page.
const { formatCurrency } = useFormatLangue();

const props = defineProps<{
  product: ShopProduct;
  showRemainingStock: boolean;
  canBuy: boolean;
  /** Message affiché quand l'achat est fermé (fenêtre de vente / clôture). */
  saleClosedMessage?: string | null;
  /** Plafond ajoutable (stock − panier, max_per_order − panier). */
  getMaxForLine: (
    product: ShopProduct,
    variant: ShopProductVariant | null,
  ) => number;
}>();

/**
 * Charge utile d'un ajout au panier — identique pour les deux actions, seule
 * la SUITE diffère (panneau latéral / page panier), et elle est décidée par la
 * page, pas ici.
 */
export interface ShopAddPayload {
  product: ShopProduct;
  variant: ShopProductVariant | null;
  quantity: number;
  custom_price?: number;
  custom_data?: Record<string, unknown>;
}

/**
 * Les deux actions transportent la MÊME charge utile ; seule la suite diffère,
 * et c'est la page qui la décide :
 *  · `add`     → ouverture du panneau latéral (on continue ses achats) ;
 *  · `buy-now` → navigation directe vers la page panier (un seul article).
 * Signature unique (et non deux surcharges) : `submitPurchase` peut alors
 * relayer son `intent` tel quel, sans aiguillage ni `as`.
 */
const emit = defineEmits<{
  (e: "add" | "buy-now", payload: ShopAddPayload): void;
}>();

const productRef = computed(() => props.product);
const canBuyRef = computed(() => props.canBuy);

// Documents demandés sur le produit : rattachés à cette boutique.
const urlEnvoiDocument = useUrlEnvoiDocument("shops");

const selection = useShopProductSelection({
  product: productRef,
  canBuy: canBuyRef,
  getMaxForLine: (product, variant) => props.getMaxForLine(product, variant),
});

const {
  selectedValues,
  quantity,
  customPrice,
  customData,
  highlightedAxis,
  reset,
  axes,
  missingAxes,
  firstMissingAxis,
  valueState,
  valueAriaLabel,
  valueTitle,
  selectValue,
  isDigital,
  minPrice,
  unitPrice,
  priceLabel,
  priceIsIndicative,
  lineTotal,
  flexibleTooLow,
  remainingForDisplay,
  remainingIsPartial,
  maxQuantity,
  decrementQuantity,
  incrementQuantity,
  lineCustomFields,
  canAdd,
  blockingReason,
  buildAddPayload,
} = selection;

// Réinitialise l'état quand on passe d'un produit à l'autre (navigation entre
// deux fiches sans démonter la page).
watch(() => props.product.id, () => reset(), { immediate: true });

/** Seuil « il n'en reste presque plus » — parité BslugStepTickets (≤ 10). */
const LOW_STOCK_THRESHOLD = 10;
const showRemaining = computed(
  () =>
    props.showRemainingStock &&
    !isDigital.value &&
    remainingForDisplay.value !== null,
);

/**
 * Axes rendus en PASTILLES DE COULEUR (canon Zalando/Uniqlo) plutôt qu'en
 * boutons texte. La règle de bascule et son repli vivent dans
 * `~/utils/shopColorSwatch` : nom d'axe « Couleur »/« Coloris »… ET 100 % des
 * valeurs reconnues par le dictionnaire FR. Une seule valeur inconnue
 * (« Édition limitée ») → l'axe ENTIER reste en texte, aucune couleur inventée.
 *
 * Les autres axes (Taille, Matière…) gardent impérativement le rendu texte
 * compact existant (`px-2 min-h-9`).
 */
const colorAxisNames = computed(
  () =>
    new Set(
      axes.value.filter((a) => shouldRenderAsColorSwatches(a)).map((a) => a.name),
    ),
);

function isColorAxis(axisName: string): boolean {
  return colorAxisNames.value.has(axisName);
}

/**
 * Aplat d'une valeur. Non-null par construction sur un axe couleur (la bascule
 * exige que TOUTES les valeurs soient résolues) ; le repli neutre n'existe que
 * pour satisfaire le typage sans `any` ni `!`.
 */
function swatchFor(value: string): ShopColorSwatch {
  return resolveShopColorSwatch(value) ?? { hex: "#9096a1", hex2: null };
}

/**
 * Style d'une pastille. Aplats PLEINS uniquement (règle
 * `ui_no_bg_transparency`) : une bicolore est peinte en deux moitiés via un
 * `linear-gradient` à arrêt franc — deux couleurs opaques, aucune opacité.
 */
function swatchStyle(value: string): Record<string, string> {
  const swatch = swatchFor(value);
  return {
    background: swatch.hex2
      ? `linear-gradient(135deg, ${swatch.hex} 0 50%, ${swatch.hex2} 50% 100%)`
      : swatch.hex,
  };
}

/**
 * Style de la coche : encre CALCULÉE sur la luminance de l'aplat (jamais
 * blanche en dur — sur « Blanc » ou « Jaune » elle serait invisible), plus un
 * détourage sur les bicolores, où la coche chevauche deux aplats opposés.
 */
function swatchCheckStyle(value: string): Record<string, string> {
  const swatch = swatchFor(value);
  const halo = swatchInkHalo(swatch);
  const style: Record<string, string> = { color: swatchInk(swatch) };
  if (halo) {
    style.textShadow = `0 0 2px ${halo}, 0 0 2px ${halo}, 0 0 2px ${halo}`;
  }
  return style;
}

/**
 * Une pastille claire (blanc, écru, beige) disparaît sur la carte blanche de
 * la fiche : elle reçoit alors une bordure grise MARQUÉE au lieu de la bordure
 * discrète des couleurs foncées.
 */
function swatchNeedsStrongBorder(value: string): boolean {
  return swatchNeedsOutline(swatchFor(value));
}

/** Ancres des sélecteurs d'axe, pour le scroll de feedback (défaut c). */
const axisRefs = ref<Record<string, HTMLElement | null>>({});
function setAxisRef(name: string, el: Element | ComponentPublicInstance | null) {
  axisRefs.value[name] = (el as HTMLElement | null) ?? null;
}

/**
 * Tentative d'ajout effectuée : tant qu'elle est fausse, aucun axe n'affiche
 * d'erreur (au chargement, ne rien avoir choisi est normal, pas fautif).
 * Une fois vraie, TOUS les axes manquants sont signalés — pas seulement le
 * premier : sur un produit Taille + Couleur, corriger l'un pour découvrir
 * ensuite que l'autre manque aussi serait une correction en deux temps.
 */
const addAttempted = ref(false);

/**
 * L'erreur disparaît dès que tous les axes sont renseignés : garder un message
 * rouge alors que l'acheteur vient de corriger serait un reproche qui traîne.
 */
watch(
  () => axes.value.every((a) => !!selectedValues.value[a.name]),
  (complete) => {
    if (complete) addAttempted.value = false;
  },
);

/**
 * VALIDATION PARTAGÉE par les DEUX actions d'achat (« Acheter maintenant » et
 * « Ajouter au panier »). Les deux boutons appellent cette unique fonction :
 * ils ne peuvent donc pas diverger, ni sur ce qui est accepté, ni sur le
 * guidage produit quand la sélection est incomplète.
 *
 * Aucun des deux n'est désactivé pour une sélection incomplète (NN/G) : au
 * clic, on GUIDE — `addAttempted` déclenche le message rouge sous CHAQUE axe
 * manquant, et l'on défile jusqu'au premier d'entre eux. Ce n'est que quand
 * l'achat est réellement impossible (boutique close, sélection épuisée,
 * paiement non configuré) que les boutons sont désactivés, tous les deux
 * ensemble (`purchaseBlocked`).
 *
 * `intent` ne change QUE l'événement émis, jamais la règle de validation : la
 * page décide de la suite (panneau latéral ou page panier).
 */
function submitPurchase(intent: "add" | "buy-now") {
  const payload = buildAddPayload();
  if (payload) {
    emit(intent, payload);
    return;
  }
  addAttempted.value = true;
  const target = firstMissingAxis.value;
  if (!target) return;
  highlightedAxis.value = target;
  void nextTick(() => {
    const el = axisRefs.value[target];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

/**
 * Achat RÉELLEMENT impossible — le seul cas où les boutons se désactivent.
 * Une sélection simplement incomplète (`missingAxes`) n'en fait pas partie :
 * elle se corrige par le guidage ci-dessus. Expression unique, partagée par
 * les deux boutons, pour qu'ils s'éteignent toujours ensemble.
 */
const purchaseBlocked = computed(
  () => !props.canBuy || (missingAxes.value.length === 0 && !canAdd.value),
);
</script>

<template>
  <div class="space-y-5">
    <!-- Achat fermé (hors fenêtre de vente) : fiche consultable, message clair -->
    <BslugStatusNotice
      v-if="saleClosedMessage"
      variant="inline"
      tone="red"
      icon="i-lucide-clock"
      :message="saleClosedMessage"
    />

    <!--
      Produit NUMÉRIQUE (migration 166) — argument de vente n°1 : le fichier
      est disponible immédiatement après paiement. Ni variantes ni stock.
    -->
    <div
      v-if="isDigital"
      class="flex items-start gap-2.5 rounded-2xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950 px-3 py-2.5"
    >
      <UIcon name="i-lucide-download" class="size-4 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
      <div class="min-w-0">
        <p class="text-sm font-semibold text-blue-900 dark:text-blue-200">
          {{ $t("boutique.commun.telechargementImmediat") }}
        </p>
        <p class="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
          {{ $t("boutique.productBuyPanel.digitalDescription") }}
        </p>
      </div>
    </div>

    <!--
      Sélecteurs de variantes. `space-y-5` sépare franchement DEUX AXES
      (Taille / Couleur) : dans la version précédente les deux blocs se
      touchaient, et le titre du second se lisait comme la suite du premier.
    -->
    <div v-if="axes.length > 0" class="space-y-5">
      <!--
        Pas d'encadré ni de fond de « mise en évidence » : un axe non choisi
        n'est pas un état visuel à décorer, c'est une ERREUR DE FORMULAIRE.
        Elle se signale donc comme partout ailleurs — un message rouge sous le
        champ concerné (voir plus bas) — et le bloc ne change jamais de taille,
        donc la page ne saute pas.
      -->
      <div
        v-for="axis in axes"
        :key="axis.name"
        :ref="(el) => setAxisRef(axis.name, el)"
        class="space-y-2 scroll-mt-24"
      >
        <!--
          Intitulé « TAILLE : M » — la valeur choisie est COLLÉE au nom de
          l'axe, pour tous les axes (Karim 2026-08-09). Elle était auparavant
          renvoyée à l'autre bout de la ligne sur les axes texte : l'œil devait
          traverser toute la largeur pour relier « TAILLE » à « M », alors que
          les deux informations n'ont de sens qu'ensemble.
          « À choisir » reste à droite tant que rien n'est sélectionné : c'est
          une consigne, pas une valeur — elle n'a pas à occuper la place du
          résultat.
        -->
        <div class="flex items-baseline justify-between gap-2">
          <p class="text-xs font-semibold text-[#505870] dark:text-gray-300 uppercase tracking-wide min-w-0">
            {{ axis.name }}
            <template v-if="selectedValues[axis.name]">
              <span aria-hidden="true">:</span>
              <span class="normal-case text-sm text-[#2d2e5c] dark:text-white font-bold">
                {{ selectedValues[axis.name] }}
              </span>
            </template>
            <span v-else class="text-red-600 dark:text-red-400">*</span>
          </p>
          <p
            v-if="!selectedValues[axis.name]"
            class="text-xs text-[#717198] dark:text-gray-400 shrink-0"
          >
            {{ $t("boutique.productBuyPanel.aChoisir") }}
          </p>
        </div>

        <!--
          AXE COULEUR — pastilles rondes remplies de la couleur réelle (déduite
          du nom, cf. `~/utils/shopColorSwatch`). La pastille n'ayant AUCUN
          libellé visible, `aria-label` est la seule information disponible pour
          un lecteur d'écran : il est donc obligatoire, et identique à celui du
          rendu texte (état épuisé/indisponible compris).

          Les trois états restent DISTINCTS, comme sur l'axe texte :
           · sélectionné  → anneau accent + coche contrastée ;
           · épuisé       → badge TEXTUEL « Épuisé » sous la pastille (jamais un
             barré, jamais un signal purement visuel — canon BslugStepTickets) ;
           · indisponible → bordure pointillée + `title` explicite, sans badge.

          Cible tactile : la pastille mesure 32 px mais le bouton qui la porte
          fait 44×44 px (`min-h-11 min-w-11`, Baymard) — confortable au doigt
          sans grossir le visuel.
        -->
        <div v-if="isColorAxis(axis.name)" class="flex flex-wrap gap-1">
          <button
            v-for="value in axis.values"
            :key="value"
            type="button"
            class="relative h-14 min-w-11 rounded-xl flex items-start justify-center pt-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-500"
            :class="valueState(axis.name, value) !== 'available' ? 'cursor-not-allowed' : ''"
            :disabled="valueState(axis.name, value) !== 'available'"
            :aria-disabled="valueState(axis.name, value) !== 'available'"
            :aria-pressed="selectedValues[axis.name] === value"
            :aria-label="valueAriaLabel(axis.name, value)"
            :title="valueTitle(axis.name, value)"
            @click="selectValue(axis.name, value)"
          >
            <span
              class="size-8 rounded-full flex items-center justify-center border-2 transition-shadow"
              :class="[
                selectedValues[axis.name] === value
                  ? 'shop-swatch-selected'
                  : valueState(axis.name, value) === 'unavailable'
                    ? 'border-dashed border-gray-300 dark:border-[#3a3a3a]'
                    : swatchNeedsStrongBorder(value)
                      ? 'border-gray-400 dark:border-gray-500'
                      : 'border-gray-200 dark:border-[#2a2a2a]',
              ]"
              :style="swatchStyle(value)"
            >
              <UIcon
                v-if="selectedValues[axis.name] === value"
                name="i-lucide-check"
                class="size-4"
                :style="swatchCheckStyle(value)"
                aria-hidden="true"
              />
            </span>
            <!-- Badge TEXTUEL conservé : un signal uniquement visuel serait un
                 retour en arrière (G117). -->
            <!--
              Badge SORTI DU FLUX (`absolute`) : dans le flux, il poussait sa
              pastille vers le haut et cassait l'alignement de la rangée — une
              couleur épuisée n'était plus sur la même ligne que les autres.
            -->
            <span
              v-if="valueState(axis.name, value) === 'sold_out'"
              aria-hidden="true"
              class="absolute left-1/2 -translate-x-1/2 bottom-0 inline-flex items-center px-1 rounded-full text-[9px] font-semibold leading-tight bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300"
            >
              {{ $t("boutique.commun.epuise") }}
            </span>
          </button>
        </div>

        <div v-else class="flex flex-wrap gap-1">
          <!--
            Trois états VISUELLEMENT distincts (défaut a) :
             · sélectionné   → fond accent ;
             · épuisé        → cliquable non, badge « Épuisé » explicite ;
             · indisponible  → désactivé + title (« n'existe pas avec … »).

            DENSITÉ (canon Zalando/Shopify) : la pastille se dimensionne sur son
            libellé — « S » reste étroite, « Palmier » s'étale. La cible tactile
            de 44×44 px (Baymard) est obtenue par `min-h-11`/`min-w-11`, JAMAIS
            par du padding : gonfler `px` élargirait aussi « Palmier » pour rien.
          -->
          <button
            v-for="value in axis.values"
            :key="value"
            type="button"
            class="relative px-2 min-h-9 min-w-9 rounded-lg text-sm font-semibold border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-500"
            :class="[
              selectedValues[axis.name] === value
                ? 'border-transparent'
                : valueState(axis.name, value) === 'unavailable'
                  ? 'border-dashed border-gray-200 dark:border-[#2a2a2a] text-gray-300 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-[#121212]'
                  : valueState(axis.name, value) === 'sold_out'
                    ? 'border-gray-200 dark:border-[#2a2a2a] text-gray-400 dark:text-gray-500 cursor-not-allowed bg-gray-50 dark:bg-[#121212] pr-[3.5rem]'
                    : 'border-gray-300 dark:border-[#333] text-[#505870] dark:text-gray-300 hover:border-gray-500 dark:hover:border-gray-400 bg-white dark:bg-[#141414]',
            ]"
            :style="selectedValues[axis.name] === value
              ? { background: 'var(--accent, #212121)', color: 'var(--accent-text, #fff)' }
              : undefined"
            :disabled="valueState(axis.name, value) !== 'available'"
            :aria-disabled="valueState(axis.name, value) !== 'available'"
            :aria-pressed="selectedValues[axis.name] === value"
            :aria-label="valueAriaLabel(axis.name, value)"
            :title="valueTitle(axis.name, value)"
            @click="selectValue(axis.name, value)"
          >
            {{ value }}
            <!-- Badge textuel : canon BslugStepTickets, JAMAIS de barré (G117). -->
            <span
              v-if="valueState(axis.name, value) === 'sold_out'"
              aria-hidden="true"
              class="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300"
            >
              {{ $t("boutique.commun.epuise") }}
            </span>
          </button>
        </div>

        <!--
          Erreur de formulaire classique : message rouge sous le champ, affiché
          seulement après une tentative d'ajout (pas au chargement, où rien
          n'est encore choisi et où ce serait un reproche gratuit).
        -->
        <p
          v-if="addAttempted && !selectedValues[axis.name]"
          class="text-xs font-medium text-red-600 dark:text-red-400"
          role="alert"
        >
          {{ $t("boutique.productBuyPanel.choisirOption") }}
        </p>
      </div>
    </div>

    <!--
      Stock restant — affiché dès qu'il est CALCULABLE (défaut e), y compris
      sur une sélection partielle (« 8 restants sur les tailles disponibles »).
    -->
    <p
      v-if="showRemaining"
      class="text-xs tabular-nums flex items-center gap-1.5 transition-colors duration-300"
      :class="remainingForDisplay! <= LOW_STOCK_THRESHOLD
        ? 'text-red-600 dark:text-red-400 font-semibold'
        : 'text-[#505870] dark:text-gray-400'"
    >
      <UIcon name="i-lucide-box" class="size-3.5 shrink-0" />
      <template v-if="remainingForDisplay! > 0">
        {{ remainingForDisplay }}
        {{ remainingForDisplay! > 1 ? "articles restants" : "article restant" }}
        <span v-if="remainingIsPartial" class="text-[#717198] dark:text-gray-500 font-normal">
          {{ $t("boutique.productBuyPanel.auTotal") }}
        </span>
      </template>
      <template v-else>{{ $t("boutique.commun.epuise") }}</template>
    </p>

    <!-- Prix libre -->
    <div
      v-if="product.pricing === 'flexible'"
      class="rounded-2xl border border-gray-200 dark:border-[#262626] bg-gray-50 dark:bg-[#0f0f0f] p-3"
    >
      <label for="shop-custom-price" class="block text-xs font-semibold text-[#2d2e5c] dark:text-white mb-2">
        {{ $t("boutique.productBuyPanel.choisirMontant") }}
      </label>
      <div class="relative max-w-[200px]">
        <input
          id="shop-custom-price"
          v-model.number="customPrice"
          type="number"
          inputmode="decimal"
          :min="minPrice || 0"
          step="0.01"
          placeholder="0.00"
          class="w-full h-11 pl-3 pr-10 rounded-xl border bg-white dark:bg-[#141414] text-base font-semibold text-[#2d2e5c] dark:text-white focus:outline-none tabular-nums transition-colors"
          :class="flexibleTooLow ? 'border-red-500 dark:border-red-500' : 'border-gray-200 dark:border-[#262626] focus:border-[#212121] dark:focus:border-white'"
        />
        <span class="absolute right-3 top-1/2 -translate-y-1/2 text-base font-medium text-gray-500 pointer-events-none">€</span>
      </div>
      <p
        v-if="minPrice > 0"
        class="text-[11px] mt-2 flex items-center gap-1"
        :class="flexibleTooLow ? 'text-red-600 dark:text-red-400 font-medium' : 'text-[#505870] dark:text-gray-400'"
      >
        <UIcon name="i-lucide-info" class="size-3" />
        {{ $t("boutique.productBuyPanel.minimum", { montant: formatCurrency(minPrice) }) }}
      </p>
    </div>

    <!-- Champs perso de la ligne (1× par ligne, pas par exemplaire) -->
    <CustomFieldsForm
      v-if="lineCustomFields.length > 0"
      v-model="customData"
      :fields="(lineCustomFields as unknown as EventCustomFieldDef[])"
      :url-envoi-document="urlEnvoiDocument"
    />

    <p v-if="product.max_per_order" class="text-[11px] text-[#505870] dark:text-gray-400">
      {{ $t("boutique.productBuyPanel.maximumParCommande", { n: product.max_per_order }) }}
    </p>

    <!--
      BARRE D'ACTION COLLANTE (défaut f) : prix courant + quantité + bouton
      toujours atteignables, mobile comme desktop, quelle que soit la longueur
      de la description. `pb-[env(safe-area-inset-bottom)]` pour l'encoche iOS.
    -->
    <div
      class="sticky bottom-0 -mx-4 md:-mx-6 px-4 md:px-6 pt-3 pb-3 bg-white dark:bg-[#141414] border-t border-gray-100 dark:border-gray-800 z-20"
      style="padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px))"
    >
      <div class="flex items-center justify-between gap-3">
        <!-- Prix courant, à portée immédiate de l'action (défaut d) -->
        <div class="min-w-0">
          <p
            class="text-lg font-bold truncate"
            :class="unitPrice > 0 || priceIsIndicative
              ? 'text-[#2d2e5c] dark:text-white'
              : 'text-emerald-600 dark:text-emerald-400'"
          >
            {{ priceLabel }}
          </p>
          <p
            v-if="quantity > 1 && lineTotal > 0 && !priceIsIndicative"
            class="text-[11px] text-[#505870] dark:text-gray-400 tabular-nums"
          >
            {{ $t("boutique.productBuyPanel.total", { montant: formatCurrency(lineTotal) }) }}
          </p>
          <p
            v-else-if="product.vat_rate != null && unitPrice > 0"
            class="text-[11px] text-[#505870] dark:text-gray-400"
          >
            {{ $t("boutique.productBuyPanel.dontTva", { taux: product.vat_rate }) }}
          </p>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <!-- Quantité — cibles 44×44 px (Baymard). Elle reste sur la LIGNE DU
               PRIX : « combien » et « à quel prix » se lisent ensemble, et les
               deux boutons d'action récupèrent toute la largeur en dessous. -->
          <div class="flex items-center gap-1">
            <button
              type="button"
              :aria-label="$t('boutique.productBuyPanel.ariaDiminuerQuantite')"
              class="w-11 h-11 rounded-xl border border-gray-300 dark:border-gray-600 flex items-center justify-center text-[#2d2e5c] dark:text-white disabled:text-gray-400 disabled:dark:text-gray-600 disabled:cursor-not-allowed transition-colors"
              :disabled="quantity <= 1"
              @click="decrementQuantity"
            >
              <UIcon name="i-lucide-minus" class="size-4" />
            </button>
            <span
              class="w-7 text-center text-base font-bold text-[#2d2e5c] dark:text-white tabular-nums"
              aria-live="polite"
              :aria-label="$t('boutique.productBuyPanel.ariaQuantite', { n: quantity })"
            >{{ quantity }}</span>
            <button
              type="button"
              :aria-label="$t('boutique.productBuyPanel.ariaAugmenterQuantite')"
              class="w-11 h-11 rounded-xl border border-gray-300 dark:border-gray-600 flex items-center justify-center text-[#2d2e5c] dark:text-white disabled:text-gray-400 disabled:dark:text-gray-600 disabled:cursor-not-allowed transition-colors"
              :disabled="quantity >= maxQuantity"
              @click="incrementQuantity"
            >
              <UIcon name="i-lucide-plus" class="size-4" />
            </button>
          </div>

        </div>
      </div>

      <!--
        DEUX ACTIONS EMPILÉES (canon e-commerce : AliExpress, Amazon, Zalando).

        POURQUOI SOUS LA LIGNE PRIX/QUANTITÉ, ET PLEINE LARGEUR :
        les deux boutons ne tenaient pas à droite du prix — à eux deux ils
        auraient laissé au primaire une quinzaine de caractères sur mobile, donc
        un « Acheter » tronqué et deux cibles étroites collées l'une à l'autre.
        Empilés pleine largeur, chacun garde son libellé ENTIER (aucun repli
        « Ajouter » à la place de « Ajouter au panier ») et une cible de 44 px
        sur toute la largeur du panneau — le geste le plus sûr au pouce. La
        ligne du dessus continue de porter prix et quantité, inchangés.

        HIÉRARCHIE : « Acheter maintenant » est l'aplat d'accent (action
        principale), « Ajouter au panier » une bordure sur fond blanc plein
        (secondaire). Aucune couleur semi-transparente (`ui_no_bg_transparency`).

        Les DEUX appellent `submitPurchase` : même validation, même guidage
        quand la sélection est incomplète, même désactivation (`purchaseBlocked`)
        quand l'achat est réellement impossible.
      -->
      <div class="mt-3 flex flex-col gap-2">
        <button
          type="button"
          data-testid="btn-buy-now"
          class="w-full min-h-11 px-4 accent-btn rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          :disabled="purchaseBlocked"
          :aria-describedby="blockingReason ? 'shop-add-hint' : undefined"
          @click="submitPurchase('buy-now')"
        >
          <UIcon name="i-lucide-zap" class="size-4" />
          {{ $t("boutique.productBuyPanel.acheterMaintenant") }}
        </button>

        <button
          type="button"
          data-testid="btn-add-to-cart"
          class="w-full min-h-11 px-4 secondary-btn rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          :disabled="purchaseBlocked"
          :aria-describedby="blockingReason ? 'shop-add-hint' : undefined"
          @click="submitPurchase('add')"
        >
          <UIcon name="i-lucide-shopping-cart" class="size-4" />
          {{ $t("boutique.productBuyPanel.ajouterAuPanier") }}
        </button>
      </div>

      <!-- Message qui NOMME le critère manquant (défaut c) -->
      <p
        v-if="blockingReason"
        id="shop-add-hint"
        role="status"
        aria-live="polite"
        class="text-xs mt-2 flex items-center gap-1.5"
        :class="addAttempted
          ? 'text-red-600 dark:text-red-400 font-medium'
          : 'text-[#505870] dark:text-gray-400'"
      >
        <UIcon
          v-if="addAttempted"
          name="i-lucide-alert-circle"
          class="size-3.5 shrink-0"
        />
        {{ blockingReason }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Pastille SÉLECTIONNÉE : anneau d'accent DÉTACHÉ de l'aplat. La bordure
   blanche/foncée intercalée est indispensable — sans elle, une pastille de la
   même teinte que l'accent (asso au thème bleu vendant un t-shirt bleu) se
   fondrait dans son propre anneau et la sélection deviendrait invisible.
   `box-shadow` (et non `outline`) pour épouser exactement le cercle.
   Aucune opacité : ce sont deux aplats pleins superposés. */
.shop-swatch-selected {
  border-color: #ffffff;
  box-shadow: 0 0 0 2px var(--accent, #212121);
}
.dark .shop-swatch-selected {
  border-color: #141414;
}

.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}

/* Survol en COULEUR PLEINE (jamais d'`opacity`, qui laisse transparaître le
   fond) : un voile noir de 12 % posé par-dessus l'accent via `color-mix`
   assombrit le bouton sans le rendre translucide, quelle que soit la couleur
   choisie par l'association. */
.accent-btn:not(:disabled):hover {
  background: color-mix(in srgb, var(--accent, #212121) 88%, #000);
}

/* Désactivé = gris PLEIN explicite. `opacity: .5` donnait un contraste
   insuffisant (texte blanc délavé sur fond clair) et laissait deviner la
   couleur de l'accent : on lit alors « bouton actif un peu pâle » au lieu de
   « bouton indisponible ». */
.accent-btn:disabled {
  background: #e5e7eb; /* gray-200 */
  color: #6b7280; /* gray-500 */
}
.dark .accent-btn:disabled {
  background: #262626;
  color: #9ca3af; /* gray-400 */
}

/* ACTION SECONDAIRE « Ajouter au panier » — bordure + fond BLANC PLEIN, jamais
   une teinte d'accent diluée : l'accent est choisi par l'association et peut
   être clair, un aplat affaibli deviendrait illisible et rivaliserait avec
   l'action principale. Le contraste entre les deux vient donc de la FORME
   (plein vs bordé), pas d'une opacité (`ui_no_bg_transparency`). */
.secondary-btn {
  background: #ffffff;
  color: #2d2e5c;
  border: 1px solid #d1d5db; /* gray-300 */
}
.dark .secondary-btn {
  background: #141414;
  color: #ffffff;
  border-color: #333333;
}
.secondary-btn:not(:disabled):hover {
  background: #f3f4f6; /* gray-100 */
  border-color: #9ca3af; /* gray-400 */
}
.dark .secondary-btn:not(:disabled):hover {
  background: #1e1e1e;
  border-color: #6b7280; /* gray-500 */
}

/* Désactivé = gris PLEIN, cohérent avec `.accent-btn:disabled` : les deux
   boutons s'éteignent ensemble et se ressemblent une fois éteints. */
.secondary-btn:disabled {
  background: #f3f4f6; /* gray-100 */
  color: #9ca3af; /* gray-400 */
  border-color: #e5e7eb; /* gray-200 */
}
.dark .secondary-btn:disabled {
  background: #1a1a1a;
  color: #6b7280; /* gray-500 */
  border-color: #262626;
}
</style>
